import "animate.css";
import "bootstrap/dist/css/bootstrap.min.css";
import "../App.css";
import { useState, useEffect } from "react";
import { useDispatch } from "react-redux";
import { signup, googleAuth } from '../store/authSlice.js';
import { useNavigate, useLocation } from "react-router-dom";
import { toast } from "react-toastify";

const GoogleIcon = () => (
  <svg className="me-2" width="18" height="18" viewBox="0 0 18 18">
    <path fill="#4285F4" d="M17.64 9.2c0-.637-.057-1.251-.164-1.84H9v3.481h4.844c-.209 1.125-.843 2.078-1.796 2.717v2.258h2.908c1.702-1.567 2.684-3.874 2.684-6.615z"/>
    <path fill="#34A853" d="M9 18c2.43 0 4.467-.806 5.956-2.184l-2.908-2.258c-.806.54-1.837.86-3.048.86-2.344 0-4.328-1.584-5.036-3.711H.957v2.332C2.438 15.983 5.482 18 9 18z"/>
    <path fill="#FBBC05" d="M3.964 10.707c-.18-.54-.282-1.117-.282-1.707s.102-1.167.282-1.707V4.961H.957C.347 6.175 0 7.55 0 9s.348 2.825.957 4.039l3.007-2.332z"/>
    <path fill="#EA4335" d="M9 3.58c1.321 0 2.508.454 3.44 1.345l2.582-2.58C13.463.891 11.426 0 9 0 5.482 0 2.438 2.017.957 4.961L3.964 7.293C4.672 5.163 6.656 3.58 9 3.58z"/>
  </svg>
);

const Signup = () => {
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [phone, setPhone] = useState("");
  const [loading, setLoading] = useState(false);

  // Google profile completion state
  const [googleStep, setGoogleStep] = useState(false);
  const [googleEmail, setGoogleEmail] = useState("");

  const dispatch = useDispatch();
  const navigate = useNavigate();
  const location = useLocation();

  // If redirected from Signin after Google auth with needsProfile: true
  useEffect(() => {
    const googleUser = location.state?.googleUser;
    if (googleUser) {
      setGoogleStep(true);
      setGoogleEmail(googleUser.email);
      setEmail(googleUser.email);
      setName(googleUser.name || "");
    }
  }, [location.state]);

  const handleSignup = async (e) => {
    e.preventDefault();
    if (!name || !email || !password || !phone) {
      toast.error("Please fill in all fields");
      return;
    }
    if (password.length < 6) {
      toast.error("Password must be at least 6 characters");
      return;
    }
    setLoading(true);
    try {
      await dispatch(signup({ email, password, name, phone })).unwrap();
      toast.success("Account created successfully!");
      navigate("/");
    } catch (err) {
      toast.error(err || "Signup failed");
    } finally {
      setLoading(false);
    }
  };

  const handleCompleteGoogleProfile = async (e) => {
    e.preventDefault();
    if (!name || !phone || !password) {
      toast.error("Please fill in all fields");
      return;
    }
    if (password.length < 6) {
      toast.error("Password must be at least 6 characters");
      return;
    }
    setLoading(true);
    try {
      await dispatch(signup({ email: googleEmail, password, name, phone, isGoogleUser: true })).unwrap();
      toast.success("Profile completed! Welcome to AARNAYOURS");
      navigate("/");
    } catch (err) {
      toast.error(err || "Failed to complete profile");
    } finally {
      setLoading(false);
    }
  };

  const handleGoogleSignup = async () => {
    setLoading(true);
    try {
      const result = await dispatch(googleAuth()).unwrap();
      if (result.needsProfile) {
        // New Google user — show profile completion form
        setGoogleStep(true);
        setGoogleEmail(result.email);
        setEmail(result.email);
        setName(result.name || "");
        toast.info("Please complete your profile to continue");
      } else {
        // Existing user — direct login
        toast.success("Signed in with Google");
        navigate("/");
      }
    } catch (err) {
      toast.error(err || "Google sign up failed");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="container-fluid vh-100">
      <div className="row h-100">
        <div className="col-md-6 d-none d-md-flex align-items-center justify-content-center">
          <img
            src="/slider/2.jpeg"
            alt="Aarnayours"
            className="img-fluid animate__animated animate__fadeInLeft"
            style={{ maxHeight: "80%", borderRadius: "24px" }}
          />
        </div>

        <div className="col-md-6 d-flex align-items-center justify-content-center">
          <div className="w-100 animate__animated animate__fadeInUp" style={{ maxWidth: "420px" }}>
            <h2 className="fw-bold mb-2 text-center">
              {googleStep ? "Complete Your Profile" : "Create Account"}
            </h2>
            <p className="text-muted text-center mb-4">
              {googleStep ? "Add your details to finish setup" : "Enter your details to get started"}
            </p>

            {googleStep && (
              <div className="alert alert-info rounded-3 mb-3 d-flex align-items-center">
                <GoogleIcon />
                <small>Google account: <strong>{googleEmail}</strong></small>
              </div>
            )}

            <form onSubmit={googleStep ? handleCompleteGoogleProfile : handleSignup}>
              <input
                type="text"
                className="form-control mb-3 rounded-pill"
                placeholder="Full Name"
                value={name}
                onChange={(e) => setName(e.target.value)}
                required
              />

              {/* Email — shown for manual signup only, locked for Google */}
              {!googleStep && (
                <input
                  type="email"
                  className="form-control mb-3 rounded-pill"
                  placeholder="Email address"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  required
                />
              )}

              <input
                type="tel"
                className="form-control mb-3 rounded-pill"
                placeholder="Phone number"
                value={phone}
                onChange={(e) => setPhone(e.target.value)}
                required
              />

              <input
                type="password"
                className="form-control mb-3 rounded-pill"
                placeholder="Password (min 6 characters)"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                required
              />

              <button
                type="submit"
                className="btn btn-dark w-100 rounded-pill mb-3"
                disabled={loading}
              >
                {loading
                  ? "Please wait..."
                  : googleStep
                  ? "Complete Profile"
                  : "Sign Up"}
              </button>
            </form>

            {!googleStep && (
              <>
                <div className="text-center mb-3"><span className="text-muted">OR</span></div>
                <button
                  type="button"
                  className="btn btn-outline-dark w-100 rounded-pill mb-3 d-flex align-items-center justify-content-center"
                  onClick={handleGoogleSignup}
                  disabled={loading}
                >
                  <GoogleIcon />
                  Continue with Google
                </button>
              </>
            )}

            <p className="text-center small">
              Already have an account? <a href="/signin">Sign in</a>
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Signup;
